import { LogboekItem, useGetDossierLogboekById } from "@/entities/logboek";
import { Filter, FilterType } from "@/features/tijdlijn/types";
import { Icon, InfoTooltip, TimelineDate, Tooltip, TooltipContent } from "@/shared/components";
import {
    Timeline,
    TimelineDescription,
    TimelineEntries,
    TimelineEvent,
    TimelineItem,
    TimelineLine,
    TimelineMarker,
    TimelineTime,
} from "@/shared/components/ui/timeline";
import { cn, formatDateTimeRelative } from "@/shared/utils";
import { TooltipTrigger } from "@radix-ui/react-tooltip";
import { formatISO, isAfter } from "date-fns";
import { useEffect, useMemo } from "react";
import { filterPredicates } from "../filter-utils";
import { useLogboekStore } from "../logboek-gelezen-status-store";

type LogboekProps = {
    dossierNummer: string;
    activeFilters: Filter[];
    readonly?: boolean;
};

type LogboekItemProps = {
    entry: LogboekItem;
    isLastItem: boolean;
    isGelezen: boolean;
};

export function LogboekTijdlijnItem({ entry, isLastItem, isGelezen }: LogboekItemProps) {
    const [time, date] = formatDateTimeRelative(entry.datumTijd).split(",");

    return (
        <TimelineItem key={entry.id} className={cn(!isGelezen && "font-medium")} animated>
            <TimelineTime dateTime={formatISO(entry.datumTijd)}>{time}</TimelineTime>
            <TimelineMarker filled={!isGelezen} />
            <TimelineEvent className={cn(isGelezen ? "text-content-default" : "text-content-emphasis")}>{entry.titel}</TimelineEvent>
            <TimelineDate>{date}</TimelineDate>
            <TimelineDescription
                className={cn(
                    "text-s text-content-subtle relative mb-2 flex justify-between pb-2",
                    isLastItem && "border-b-default border-b"
                )}
            >
                <div className="flex flex-row items-center gap-2">
                    {entry.bron}
                    {entry.persoon && <InfoTooltip iconSize="xs">{entry.persoon}</InfoTooltip>}
                </div>
                {entry.bericht && (
                    <Tooltip>
                        <TooltipTrigger asChild className="relative top-full right-0 -translate-y-1/2">
                            <Icon name="comment" className="text-content-emphasis" />
                        </TooltipTrigger>
                        <TooltipContent>“{entry.bericht}”</TooltipContent>
                    </Tooltip>
                )}
            </TimelineDescription>
        </TimelineItem>
    );
}

const isGelezen = (entryDate: Date, laatstGelezenDatumTijd: Date | undefined) => {
    if (!laatstGelezenDatumTijd) return false;
    return isAfter(laatstGelezenDatumTijd, entryDate);
};

export function Logboek({ dossierNummer, activeFilters, readonly }: LogboekProps) {
    const { data, isPending, error } = useGetDossierLogboekById(dossierNummer);
    const logboekItems = useMemo(() => data ?? [], [data]);
    const logboekGelezenStatus = useLogboekStore((s) => s.laatstGelezen);
    const markAsRead = useLogboekStore((s) => s.markAsLaatstGelezen);
    const filterTypes = activeFilters.map((filter) => filter.type);

    useEffect(() => {
        const timer = setTimeout(() => {
            markAsRead(dossierNummer);
        }, 30000);

        return () => clearTimeout(timer);
    }, [dossierNummer, markAsRead, logboekItems]);

    const filteredEntries: LogboekItem[] = useMemo(
        () =>
            filterTypes.includes(FilterType.ALL)
                ? logboekItems
                : logboekItems.filter((entry) =>
                      filterTypes.some((type) => {
                          const predicate = filterPredicates[type];
                          return predicate ? predicate(entry) : false;
                      })
                  ),
        [filterTypes, logboekItems]
    );

    const isItemGelezen = (item: LogboekItem) => {
        const logboekLaatstGelezenDatumTijd = logboekGelezenStatus.find(
            (item) => item.dossierNummer === dossierNummer
        )?.laatstGelezenDatumTijd;
        const entryDate = item.datumTijd;

        return isGelezen(entryDate, logboekLaatstGelezenDatumTijd);
    };

    if (isPending) {
        return <p>Logboek worden geladen...</p>;
    }

    if (error) {
        return <p>Fout bij het laden van Logboek: {error.message}</p>;
    }

    return (
        <Timeline className={cn(!readonly && "pt-3")}>
            {/* When readonly, we have to hide current time and reposition the vertical line */}
            <TimelineLine
                showCurrentTime={!readonly}
                className={cn(readonly ? "h-[calc(100%-45px-40px)]" : "h-[calc(100%-45px)]", readonly && "mt-11")}
            />
            <TimelineEntries className="pt-6" data-testid="logboek-list">
                {filteredEntries.map((entry, idx) => (
                    <LogboekTijdlijnItem
                        isGelezen={isItemGelezen(entry)}
                        key={entry.id}
                        entry={entry}
                        isLastItem={idx !== filteredEntries.length - 1}
                    />
                ))}
            </TimelineEntries>
        </Timeline>
    );
}
